/*
Form Field Properties

Property Name | Type(s) | Description | Example
--------------------------------------------------------
key                       | String  | key value to identify the field
value                     | Path    | path to value
type                      | String  | 'string', 'float', 'select', 'range', 'checkbox', 'switch', 'radio' | the type of
                                        input to use
source                    | String  | the vuex getter to use to fill in the items for a select or combobox
fetch                     | String  | vuex action to fetch the items if they haven't been initialized yet
default                   | Any     | The default value to use when making new items
saveOptions               | Object  | config for saving the value
  createAction            | String  | vuex action to call when creating a new value in firestore (used for comboboxes)
  createMap[              | Array   | array of properties used to transpile the new value into a firestore value
    get                   | String  | property to get from the value we are trying to save
    set                   | String  | property to save that value to
  ]
  existingGetter          | String  | vuex getter to get existing values from.
                                        Used to check if a combobox value already exists in the store before trying to
                                          create a new
                            value
  existingGetterParameter | String  | paramater to use for the getter if needed
  newGetter               | String  | once a new value has been created in firestore, use this getter to obtain its value from vuex
  saveNew                 | Boolean | whether or not new values can be saved to firestore if they aren't found in items
  saveMap[                | Array   | like createMap, maps the value to the value we want saved to the item
    get:                  | String  | property to get from the value we are trying to save
    set:                  | String  | property to save that value to
  ]
  trackUpdates            | Array, String, Boolean | When saving, items are compared to their originals to determine if
                                                      an update needs to be done.
                                                      If no field is listed here, it is assumed the value should be
                                                      tracked, and that it should be tracked as the value property
                                                      string. If it is false, it is ignored for update purposes. Can
                                                      also be an array of strings to track multiple fields (useful when
                                                      an ID is mapped outside the value)
  values [                | Array   | Array of values to use in radios
    label                 | String  | label for the radio button
    value                 | String  | lalue for the button
  ]
attributes  | v-input attributes

*/
import getAbbreviation from './unitAbbreviations'
import { decimal, minValue } from 'vuelidate/lib/validators'

export function certification ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'certification',
    value: 'certification',
    type: 'select',
    default: 'Standard',
    classes,
    attributes: {
      label: 'Certification',
      class: inputClasses,
      cacheItems: true
    },
    values: ['FSC', 'NAUF', 'Standard']
  }
}

export function color ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'color',
    value: 'color',
    type: 'combobox',
    source: 'itemColors/itemColorNames',
    fetch: 'itemColors/get',
    classes,
    attributes: {
      label: 'Color',
      class: inputClasses
    },
    saveOptions: {
      createAction: 'itemColors/add',
      createMap: [{
        set: 'name'
      }],
      existingGetter: 'itemColors/itemColorByName',
      existingGetterParameter: 'name',
      newGetter: 'itemColors/itemColor',
      saveNew: true,
      saveMap: [
        {
          get: 'name',
          set: 'color'
        },
        {
          get: 'id',
          set: 'colorID'
        }
      ]
    },
    trackUpdates: ['color', 'colorID']
  }
}

export function computerEaseNumber ({
  classes = ['xs12'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'computerEaseNumber',
    value: 'computerEaseNumber',
    type: 'string',
    delayTouch: true,
    classes,
    attributes: {
      label: 'ComputerEase No.',
      class: inputClasses
    },
    validation: [{
      validationKey: 'isUnique',
      validationFunction: function (value) {
        console.log('CE # validationFunction: ', ...arguments)
        if (!value) return true
        if (value === '') return true
        if (this.edit && value === this.standardItem.computerEaseNumber) return true

        return new Promise((resolve, reject) => {
          let matchingItems = this.getItemByComputerEaseNumber(value)
          console.log('matchingItems: ', matchingItems)

          if (!matchingItems) resolve(true)

          if (matchingItems.length === 1 && matchingItems[0].id === this.id) resolve(true)

          resolve(!matchingItems.length)
        })
      },
      errorMessage: 'CE # must be unique'
    }]
  }
}

export function cut ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'cut',
    value: 'cut',
    type: 'select',
    default: 'Plain Sawn',
    classes,
    attributes: {
      label: 'Cut',
      class: inputClasses,
      cacheItems: true
    },
    values: ['Plain Sawn', 'Quartered', 'Rift', 'Live Sawn', 'Rotary']
  }
}

export function diameter ({
  classes = ['xs12', 'sm4', 'input--short'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'diameter',
    value: 'diameter',
    type: 'float',
    classes,
    attributes: {
      label: 'Diameter',
      suffix: 'in',
      class: inputClasses
    },
    validation: [
      {
        validationKey: 'decimal',
        validationFunction: decimal,
        errorMessage: 'This should be a number'
      }
    ]
  }
}

export function finish ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'finish',
    value: 'finish',
    type: 'combobox',
    source: 'itemFinishes/itemFinishNames',
    fetch: 'itemFinishes/get',
    classes,
    attributes: {
      label: 'Finish',
      class: inputClasses
    },
    saveOptions: {
      createAction: 'itemFinishes/add',
      createMap: [{
        set: 'name'
      }],
      existingGetter: 'itemFinishes/itemFinishByName',
      existingGetterParameter: 'name',
      newGetter: 'itemFinishes/itemFinish',
      saveNew: true,
      saveMap: [
        {
          get: 'name',
          set: 'finish'
        },
        {
          get: 'id',
          set: 'finishID'
        }
      ]
    },
    trackUpdates: ['finish', 'finishID']
  }
}

export function length ({
  classes = ['xs12', 'sm4', 'input--short', 'input--short--centered'],
  inputClasses = ['input--dense'],
  autofocus = false
} = {}) {
  return {
    key: 'length',
    value: 'length',
    type: 'float',
    required: true,
    delayTouch: true,
    classes,
    attributes: {
      label: 'Length',
      suffix: 'in',
      class: inputClasses,
      required: true
    },
    validation: [
      {
        validationKey: 'decimal',
        validationFunction: decimal,
        errorMessage: 'Length should be a number'
      },
      {
        validationKey: 'minValue',
        validationFunction: minValue(0.001),
        errorMessage: 'This needs a length'
      }
    ]
  }
}

export function loadWeight ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'loadWeight',
    value: 'loadWeight',
    type: 'float',
    classes,
    attributes: {
      label: 'Load Weight',
      suffix: 'lbs',
      class: inputClasses
    },
    validation: [
      {
        validationKey: 'decimal',
        validationFunction: decimal,
        errorMessage: 'Load Weight should be a number'
      }
    ]
  }
}

export function manufacturer ({
  classes = ['xs12', 'sm6'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'manufacturer',
    value: 'manufacturer.name',
    type: 'combobox',
    source: 'manufacturers/manufacturerNames',
    fetch: 'manufacturers/get',
    required: true,
    classes,
    attributes: {
      label: 'Manufacturer',
      required: true,
      class: inputClasses
    },
    saveOptions: {
      createAction: 'manufacturers/add',
      createMap: [{
        set: 'name'
      }],
      existingGetter: 'manufacturers/manufacturerByName',
      existingGetterParameter: 'name',
      newGetter: 'manufacturers/manufacturer',
      saveNew: true,
      saveMap: [
        {
          set: 'manufacturer'
        },
        {
          get: 'id',
          set: 'manufacturerID'
        }
      ]
    },
    trackUpdates: ['manufacturer', 'manufacturerID']
  }
}

export function manufacturerNumber ({
  classes = ['xs12', 'sm6'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    label: 'Manufacturer Part No.',
    key: 'manufacturerNumber',
    value: 'manufacturerNumber',
    type: 'string',
    classes,
    attributes: {
      label: 'Manufacturer Part No.',
      class: inputClasses
    }
  }
}

export function name ({
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'name',
    value: 'name',
    type: 'string',
    required: true,
    attributes: {
      required: true,
      label: 'Name',
      autofocus: true,
      class: inputClasses
    }
  }
}

export function species ({
  classes = ['xs12', 'sm4'],
  inputClasses = ['input--dense']
} = {}) {
  return {
    key: 'species',
    value: 'species',
    type: 'combobox',
    source: 'species/speciesNames',
    fetch: 'species/get',
    classes,
    attributes: {
      label: 'Species',
      class: inputClasses
    },
    saveOptions: {
      createAction: 'species/add',
      createMap: [{
        set: 'name'
      }],
      existingGetter: 'species/speciesByName',
      existingGetterParameter: 'name',
      newGetter: 'species/species',
      saveNew: true,
      saveMap: [
        {
          get: 'name',
          set: 'species'
        },
        {
          get: 'id',
          set: 'speciesID'
        }
      ]
    },
    trackUpdates: ['species', 'speciesID']
  }
}

export function thickness ({
  classes = ['xs12', 'sm4', 'input--short', 'input--short--centered'],
  inputClasses = ['input--dense'],
  autofocus = false,
  unit = 'inches'
} = {}) {
  const abbreviation = getAbbreviation(unit)
  return {
    key: 'thickness',
    value: 'thickness',
    type: 'float',
    required: true,
    delayTouch: true,
    classes,
    attributes: {
      label: 'Thickness',
      suffix: abbreviation,
      class: inputClasses,
      required: true,
      autofocus
    },
    validation: [
      {
        validationKey: 'decimal',
        validationFunction: decimal,
        errorMessage: 'This should be a number'
      },
      {
        validationKey: 'minValue',
        validationFunction: minValue(0.001),
        errorMessage: 'This needs a thickness'
      }
    ]
  }
}

export function width ({
  classes = ['xs12', 'sm4', 'input--short', 'input--short--centered'],
  inputClasses = ['input--dense'],
  autofocus = false
} = {}) {
  return {
    key: 'width',
    value: 'width',
    type: 'float',
    required: true,
    delayTouch: true,
    classes,
    attributes: {
      label: 'Width',
      suffix: 'in',
      class: inputClasses,
      required: true
    },
    validation: [
      {
        validationKey: 'decimal',
        validationFunction: decimal,
        errorMessage: 'This should be a number'
      },
      {
        validationKey: 'minValue',
        validationFunction: minValue(0.001),
        errorMessage: 'This needs a width'
      }
    ]
  }
}
